import React, { ReactNode, useEffect, useState } from 'react'
import {
  LuChevronDown,
  LuCircleAlert,
  LuExpand,
  LuShrink,
  LuTriangleAlert,
} from 'react-icons/lu'

import DataTable from '@/components/datatable/DataTable'
import ActionButton from '@/components/misc/ActionButton'
import CircumstancesCell from '@/components/misc/ImportReferrals/CircumstancesCell'
import ClientCell from '@/components/misc/ImportReferrals/ClientCell'
import ClientHeaderDropdown from '@/components/misc/ImportReferrals/ClientHeaderDropdown'
import FromCompanyAdviserCell from '@/components/misc/ImportReferrals/FromCompanyAdviserCell'
import ImportErrorMessage from '@/components/misc/ImportReferrals/ImportErrorMessage'
import ImportingCompanyMissingWarning from '@/components/misc/ImportReferrals/ImportingCompanyMissingWarning'
import ImportStatusCell from '@/components/misc/ImportReferrals/ImportStatusCell'
import InviteNewFromAdviserCell from '@/components/misc/ImportReferrals/InviteNewFromAdviserCell'
import InviteNewToAdviserCell from '@/components/misc/ImportReferrals/InviteNewToAdviserCell'
import ReferralDateCell from '@/components/misc/ImportReferrals/ReferralDateCell'
import SelectCell from '@/components/misc/ImportReferrals/SelectCell'
import SelectHeader from '@/components/misc/ImportReferrals/SelectHeader'
import ServiceCell from '@/components/misc/ImportReferrals/ServiceCell'
import StatusCell from '@/components/misc/ImportReferrals/StatusCell'
import ToCompanyAdviserCell from '@/components/misc/ImportReferrals/ToCompanyAdviserCell'
import Step from '@/components/misc/Step'
import { useCreateServiceMutation } from '@/components/modals/ImportHistoricalReferralsModal/hooks/useCreateServiceMutation'
import { useImportHistoricalReferralMutation } from '@/components/modals/ImportHistoricalReferralsModal/hooks/useImportHistoricalReferralMutation'
import { Button } from '@/components/ui/button'
import { DialogBody, DialogFooter } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useInvalidateReferralsQueries } from '@/hooks/invalidateReferralsQueries'
import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import useNotyf from '@/hooks/useNotyf'
import { IMPORT_FILE_NOTIFICATION_DURATION } from '@/lib/constants'
import { cn } from '@/lib/utils'
import { ReferralsImportItem } from '@/redux/slices/referralsImportBase'
import {
  bulkUpdateImportItems,
  removeImportItem,
  selectItems,
  selectReferralsImportHistorical,
  setShowImported,
  updateImportItem,
} from '@/redux/slices/referralsImportHistorical'
interface Props {
  startOverButton: ReactNode
  handleFinish: () => void
  handleAutomaticFinish: () => void
}

const ResolveReferralImports: React.FC<Props> = ({
  startOverButton,
  handleFinish,
  handleAutomaticFinish,
}) => {
  const notyf = useNotyf()
  const createServiceMutation = useCreateServiceMutation()
  const importHistoricalReferralMutation = useImportHistoricalReferralMutation()
  const invalidateReferralsQueries = useInvalidateReferralsQueries()
  const [notifyStatus, setNotifyStatus] = useState<boolean>(false)
  const [importedCountLast, setImportedCountLast] = useState<number>(0)
  const [errorsCountLast, setErrorsCountLast] = useState<number>(0)
  const [isImporting, setIsImporting] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const referralsImportHistoricalState = useAppSelector(
    selectReferralsImportHistorical,
  )

  const importedCountTotal = referralsImportHistoricalState.importItems.filter(
    (importItem) => importItem.importStatus === 'imported',
  ).length
  const errorsCountTotal = referralsImportHistoricalState.importItems.filter(
    (importItem) => importItem.importStatus === 'error',
  ).length

  const totalCount = referralsImportHistoricalState.importItems.length

  useEffect(() => {
    if (notifyStatus) {
      if (importedCountTotal === totalCount) {
        notyf.success({
          message: `All referral successfully processed`,
          duration: IMPORT_FILE_NOTIFICATION_DURATION,
        })
        setTimeout(() => {
          handleAutomaticFinish()
        }, 500)
      } else {
        const importedCount = importedCountTotal - importedCountLast
        if (importedCount > 0) {
          notyf.success({
            message:
              importedCount === 1
                ? `One referral successfully processed`
                : `${importedCount} referrals successfully processed`,
            duration: IMPORT_FILE_NOTIFICATION_DURATION,
          })
        }
        const errorsCount = errorsCountTotal - errorsCountLast
        if (errorsCount > 0) {
          notyf.errorWithoutReporting({
            message:
              errorsCount == 1
                ? `One referral failed to import`
                : `${errorsCount} referrals failed to import`,
            duration: IMPORT_FILE_NOTIFICATION_DURATION,
          })
        }
        setNotifyStatus(false)
        setImportedCountLast(importedCountTotal)
        setErrorsCountLast(errorsCountTotal)
      }
    }
  }, [
    notifyStatus,
    importedCountTotal,
    importedCountLast,
    errorsCountTotal,
    errorsCountLast,
    notyf,
    totalCount,
    handleAutomaticFinish,
  ])

  const isFinished = importedCountTotal == totalCount

  if (referralsImportHistoricalState.importItems.length === 0) {
    return <div>No import items</div>
  }

  async function processImport() {
    // Handle auto-creation of new service areas
    const importedServiceAreas: number[] = []
    const servicesMutations = []

    for (const importItem of referralsImportHistoricalState.importItems) {
      if (
        importItem.newServiceAreaId &&
        importItem.toCompany &&
        !importedServiceAreas.includes(importItem.newServiceAreaId)
      ) {
        servicesMutations.push(
          createServiceMutation.mutateAsync({
            serviceAreaId: importItem.newServiceAreaId,
            companyId: importItem.toCompany.id,
          }),
        )
        importedServiceAreas.push(importItem.newServiceAreaId)
      }
    }

    await Promise.all(servicesMutations)

    // Handle referrals imports (batched 5 at a time)
    const hasErrors = false

    const batchSize = 5

    const referralsToImport = referralsImportHistoricalState.importItems.filter(
      (importItem) =>
        importItem.importStatus === 'ready' ||
        importItem.importStatus === 'error',
    )

    for (let i = 0; i < referralsToImport.length; i += batchSize) {
      const batch = referralsToImport.slice(i, i + batchSize)
      await Promise.allSettled(
        batch.map((importItem) =>
          importHistoricalReferralMutation.mutateAsync(importItem),
        ),
      )
    }

    await invalidateReferralsQueries()

    if (hasErrors) {
      notyf.errorWithoutReporting({
        message: 'Some referrals failed to import',
        duration: IMPORT_FILE_NOTIFICATION_DURATION,
      })
    }

    setNotifyStatus(true)
  }

  function handleToggleShowImported() {
    dispatch(setShowImported(!referralsImportHistoricalState.showImported))
  }

  return (
    <>
      <DialogBody className="grow">
        <Step step="3">
          <p className="mb-2">Review import data.</p>

          <DataTable
            tableProps={{
              className: 'table-align-top mb-0 text-xs h-fit',
              striped: false,
            }}
          >
            <TableHeader>
              <TableRow>
                <TableHead className="p-0">
                  <SelectHeader
                    hasSelected={
                      referralsImportHistoricalState.selectedCount > 0
                    }
                    bulkUpdateItems={(payload) =>
                      dispatch(bulkUpdateImportItems(payload))
                    }
                  />
                </TableHead>
                <TableHead
                  style={{
                    width: '15%',
                    minWidth: '180px',
                    position: 'relative',
                    paddingRight: '20px',
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span>From Company, Adviser</span>
                    {referralsImportHistoricalState.hasInvites && (
                      <FromAdviserDropdown
                        bulkUpdateSelectedOnly={
                          referralsImportHistoricalState.canBulkUpdateSelectedOnly
                        }
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead
                  style={{
                    width: '15%',
                    minWidth: '180px',
                    position: 'relative',
                    paddingRight: '20px',
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span>To Company, Adviser</span>
                    {referralsImportHistoricalState.hasInvites && (
                      <ToAdviserDropdown
                        bulkUpdateSelectedOnly={
                          referralsImportHistoricalState.canBulkUpdateSelectedOnly
                        }
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead style={{ width: '15%' }}>Service</TableHead>
                <TableHead style={{ width: '140px' }}>Date</TableHead>
                <TableHead style={{ width: '15%' }}>Status</TableHead>
                <TableHead
                  style={{ position: 'relative', paddingRight: '20px' }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span>Client</span>

                    <ClientHeaderDropdown
                      hasUpdatableClients={
                        referralsImportHistoricalState.hasUpdatableClients
                      }
                      canPauseClientEmails={
                        referralsImportHistoricalState.canPauseClientEmails
                      }
                      bulkUpdateItems={(payload) =>
                        dispatch(bulkUpdateImportItems(payload))
                      }
                      bulkUpdateSelectedOnly={
                        referralsImportHistoricalState.canBulkUpdateSelectedOnly
                      }
                    />
                  </div>
                </TableHead>
                <TableHead style={{ minWidth: '150px' }}>
                  Circumstances
                </TableHead>
                <TableHead style={{ width: '70px' }}>Import Status</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {referralsImportHistoricalState.importItems.map(
                (importItem, index) => (
                  <ImportRow
                    importItem={importItem}
                    key={importItem.refUUID}
                    index={index}
                    bulkUpdateSelectedOnly={
                      referralsImportHistoricalState.canBulkUpdateSelectedOnly
                    }
                    showImported={referralsImportHistoricalState.showImported}
                  />
                ),
              )}
            </TableBody>
          </DataTable>

          <p className="text-dim mt-2 mb-4 text-xs">
            <LuCircleAlert className="inline-block -translate-y-0.5" /> You can
            update some fields above by moving your mouse over the field and
            clicking the value or the "change" or "edit" buttons.
          </p>
        </Step>

        <Step step="4">
          <p className="mb-2">Import referrals.</p>
        </Step>
      </DialogBody>

      <DialogFooter className="flex-wrap items-center justify-between">
        {startOverButton}
        <div className="grow"></div>
        {importedCountTotal > 0 && (
          <Button variant="link" size="sm" onClick={handleToggleShowImported}>
            {referralsImportHistoricalState.showImported ? (
              <>
                <LuShrink />
                Hide imported
              </>
            ) : (
              <>
                <LuExpand />
                Show imported
              </>
            )}
          </Button>
        )}
        <span className="text-muted-foreground">
          {importedCountTotal} / {totalCount} imported
        </span>

        {isFinished ? (
          <Button variant="success" onClick={() => handleFinish()}>
            Finish
          </Button>
        ) : (
          <ActionButton
            isProcessing={isImporting}
            variant="primary"
            className="w-full sm:w-auto"
            onClick={() => {
              setIsImporting(true)
              processImport().then(() => setIsImporting(false))
            }}
          >
            {importedCountTotal > 0 ? 'Import remaining' : 'Import'}
          </ActionButton>
        )}
      </DialogFooter>
    </>
  )
}

export default ResolveReferralImports

function FromAdviserDropdown({
  bulkUpdateSelectedOnly,
}: {
  bulkUpdateSelectedOnly: boolean
}) {
  const dispatch = useAppDispatch()
  const scope = bulkUpdateSelectedOnly ? 'selected' : 'all'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline-secondary" size="icon-xs">
          <LuChevronDown />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuGroup>
          <DropdownMenuItem
            onClick={() =>
              dispatch(
                bulkUpdateImportItems({
                  filter: { selected: bulkUpdateSelectedOnly },
                  data: { inviteFromAdviser: true },
                }),
              )
            }
          >
            <span>
              Mark <b className="font-semibold">invite automatically</b> in{' '}
              <b className="font-semibold">{scope}</b> advisers
            </span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() =>
              dispatch(
                bulkUpdateImportItems({
                  filter: { selected: bulkUpdateSelectedOnly },
                  data: { inviteFromAdviser: false },
                }),
              )
            }
          >
            <span>
              Mark <b className="font-semibold">invite later manually</b> in{' '}
              <b className="font-semibold">{scope}</b> advisers
            </span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ToAdviserDropdown({
  bulkUpdateSelectedOnly,
}: {
  bulkUpdateSelectedOnly: boolean
}) {
  const dispatch = useAppDispatch()
  const scope = bulkUpdateSelectedOnly ? 'selected' : 'all'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline-secondary" size="icon-xs">
          <LuChevronDown />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuGroup>
          <DropdownMenuItem
            onClick={() =>
              dispatch(
                bulkUpdateImportItems({
                  filter: { selected: bulkUpdateSelectedOnly },
                  data: { inviteToAdviser: true },
                }),
              )
            }
          >
            <span>
              Mark <b className="font-semibold">invite automatically</b> in{' '}
              <b className="font-semibold">{scope}</b> advisers
            </span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() =>
              dispatch(
                bulkUpdateImportItems({
                  filter: { selected: bulkUpdateSelectedOnly },
                  data: { inviteToAdviser: false },
                }),
              )
            }
          >
            <span>
              Mark <b className="font-semibold">invite later manually</b> in{' '}
              <b className="font-semibold">{scope}</b> advisers
            </span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ImportRow({
  importItem,
  index,
  bulkUpdateSelectedOnly,
  showImported,
}: {
  importItem: ReferralsImportItem
  index: number
  bulkUpdateSelectedOnly: boolean
  showImported: boolean
}) {
  const dispatch = useAppDispatch()

  const isError = importItem.importStatus === 'error'
  const isHidden =
    importItem.importStatus === 'imported' ? !showImported : false
  const cellStyle: React.CSSProperties = isError
    ? {
        background: 'var(--destructive-light)',
      }
    : index % 2
      ? {}
      : {
          background: 'var(--color-gray-50)',
        }
  let rowSpan = 1
  if (isError) rowSpan += 1
  if (!importItem.importingCompanyValid) rowSpan += 1

  return (
    <>
      <TableRow
        className={cn(
          importItem.selected ? 'table-active' : '',
          isHidden ? 'row-hiding' : 'row-visible',
        )}
      >
        <TableCell className="p-0" rowSpan={rowSpan} style={cellStyle}>
          <div>
            <SelectCell
              importItem={importItem}
              handleSelect={(refUUID, modifier) => {
                dispatch(selectItems({ refUUID, modifier }))
              }}
            />
          </div>
        </TableCell>

        <TableCell style={cellStyle}>
          <div>
            <span className="text-dim mb-0.5 block text-xs">
              Introduced by:
            </span>
            {importItem.fromCompany ? (
              <>
                {!importItem.isFromInvite && (
                  <span className="block font-medium">
                    {importItem.fromCompany.name}
                  </span>
                )}
                <FromCompanyAdviserCell
                  importItem={importItem}
                  updateImportItem={(item) => dispatch(updateImportItem(item))}
                  bulkUpdateItems={(payload) =>
                    dispatch(bulkUpdateImportItems(payload))
                  }
                  bulkUpdateSelectedOnly={bulkUpdateSelectedOnly}
                />
                {importItem.isFromInvite && (
                  <InviteNewFromAdviserCell
                    importItem={importItem}
                    updateImportItem={(item) =>
                      dispatch(updateImportItem(item))
                    }
                    isHistorical
                  />
                )}
              </>
            ) : (
              <InviteNewFromAdviserCell
                importItem={importItem}
                updateImportItem={(item) => dispatch(updateImportItem(item))}
                isHistorical
              />
            )}
          </div>
        </TableCell>

        <TableCell style={cellStyle}>
          <div>
            <span className="text-dim mb-0.5 block text-xs">
              Introduced to:
            </span>
            {importItem.toCompany ? (
              <>
                {!importItem.isToInvite && (
                  <span className="block font-medium">
                    {importItem.toCompany.name}
                  </span>
                )}
                <ToCompanyAdviserCell
                  importItem={importItem}
                  updateImportItem={(item) => dispatch(updateImportItem(item))}
                  bulkUpdateItems={(payload) =>
                    dispatch(bulkUpdateImportItems(payload))
                  }
                  bulkUpdateSelectedOnly={bulkUpdateSelectedOnly}
                />
                {importItem.isToInvite && (
                  <InviteNewToAdviserCell
                    importItem={importItem}
                    updateImportItem={(item) =>
                      dispatch(updateImportItem(item))
                    }
                    isHistorical
                  />
                )}
              </>
            ) : (
              <InviteNewToAdviserCell
                importItem={importItem}
                updateImportItem={(item) => dispatch(updateImportItem(item))}
                isHistorical
              />
            )}
          </div>
        </TableCell>

        <TableCell style={cellStyle}>
          <div>
            <ServiceCell
              importItem={importItem}
              updateImportItem={(item) => dispatch(updateImportItem(item))}
              bulkUpdateItems={(payload) =>
                dispatch(bulkUpdateImportItems(payload))
              }
              bulkUpdateSelectedOnly={bulkUpdateSelectedOnly}
            />
          </div>
        </TableCell>

        <TableCell style={cellStyle}>
          <div>
            <ReferralDateCell
              importItem={importItem}
              updateImportItem={(item) => dispatch(updateImportItem(item))}
              bulkUpdateItems={(payload) =>
                dispatch(bulkUpdateImportItems(payload))
              }
              bulkUpdateSelectedOnly={bulkUpdateSelectedOnly}
            />
          </div>
        </TableCell>

        <TableCell style={cellStyle}>
          <div>
            <StatusCell
              importItem={importItem}
              updateImportItem={(item) => dispatch(updateImportItem(item))}
              bulkUpdateItems={(payload) =>
                dispatch(bulkUpdateImportItems(payload))
              }
              bulkUpdateSelectedOnly={bulkUpdateSelectedOnly}
            />
          </div>
        </TableCell>

        <TableCell style={cellStyle}>
          <div>
            <ClientCell
              importItem={importItem}
              updateImportItem={(item) => dispatch(updateImportItem(item))}
            />
          </div>
        </TableCell>
        <TableCell style={cellStyle}>
          <div>
            <CircumstancesCell
              importItem={importItem}
              updateImportItem={(item) => dispatch(updateImportItem(item))}
            />
          </div>
        </TableCell>

        <TableCell className="relative" rowSpan={rowSpan} style={cellStyle}>
          <div>
            <ImportStatusCell
              importItem={importItem}
              removeImportItem={(item) => {
                dispatch(removeImportItem(item.refUUID))
              }}
            />
          </div>
        </TableCell>
      </TableRow>

      {!importItem.importingCompanyValid && (
        <TableRow>
          <TableCell colSpan={7} style={cellStyle}>
            <ImportingCompanyMissingWarning />
          </TableCell>
        </TableRow>
      )}

      {isError && (
        <TableRow>
          <TableCell
            colSpan={7}
            className="text-destructive relative"
            style={cellStyle}
          >
            <LuTriangleAlert className="absolute top-2 left-3 size-6" />
            {importItem.importErrors ? (
              <ul className="mb-4 ml-8">
                {Object.entries(importItem.importErrors).map(
                  ([key, value], index) => (
                    <li key={`${index}-${key}-${value}`}>
                      <ImportErrorMessage
                        importItem={importItem}
                        field={key}
                        message={value}
                      />
                    </li>
                  ),
                )}
              </ul>
            ) : (
              <p className="mb-4">Unknown error</p>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  )
}
